import { useEffect, useRef, useState } from "react";
import { RouteLocation, RouteOptions } from "../redux/services/config";

interface Props {
  featureId?: string | number;
  selectionVersion: number;
  stepFree: boolean;
  requestRoute: (stepFree: boolean) => void;
  setStepFree: (stepFree: boolean) => void;
  result: {
    isError: boolean;
    requestId?: string;
    originalArgs?: { to: RouteLocation; options?: RouteOptions };
  };
}

export function useStepFreeRouting({
  featureId,
  selectionVersion,
  stepFree,
  requestRoute,
  setStepFree,
  result,
}: Props) {
  const selection =
    featureId == null ? undefined : `${featureId}:${selectionVersion}`;
  const previousSelection = useRef<string>();
  const handledFailure = useRef<string>();
  const [failedSelection, setFailedSelection] = useState<string>();
  const callbacks = useRef({ requestRoute, setStepFree });
  useEffect(() => {
    callbacks.current = { requestRoute, setStepFree };
  });

  useEffect(() => {
    const changed = previousSelection.current !== selection;
    previousSelection.current = selection;
    if (changed) setFailedSelection(undefined);
    if (!selection || (changed && stepFree)) {
      // A new destination always starts with its normal route. A failed
      // accessibility preference must not carry over to the next POI.
      if (stepFree) callbacks.current.setStepFree(false);
      return;
    }
    if (stepFree) setFailedSelection(undefined);
    callbacks.current.requestRoute(stepFree);
  }, [selection, stepFree]);

  useEffect(() => {
    const destination = result.originalArgs?.to;
    if (
      !selection ||
      !result.isError ||
      !result.requestId ||
      handledFailure.current === result.requestId ||
      result.originalArgs?.options?.routeModifier !== "step_free" ||
      !destination ||
      !("featureId" in destination) ||
      destination.featureId !== Number(featureId)
    )
      return;

    handledFailure.current = result.requestId;
    setFailedSelection(selection);
    // Fetch a normal route even when there is no previously cached route.
    // Keep the error independent of the replacement request's status.
    callbacks.current.setStepFree(false);
  }, [
    result.isError,
    result.requestId,
    result.originalArgs,
    featureId,
    selection,
  ]);

  return {
    stepFreeUnavailable:
      selection !== undefined && failedSelection === selection,
  };
}
